import { isString } from 'lodash';
import { ReactNode, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import roundAddAPhoto from '@iconify/icons-ic/round-add-a-photo';
// material
import { alpha, Theme, styled } from '@mui/material/styles';
import { Box, Typography, Paper } from '@mui/material';
import { SxProps } from '@mui/system';
// utils
import { fData } from 'utils/formatNumber';

import { cloudinaryConfig } from 'config';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  width: 144,
  height: 144,
  margin: 'auto',
  borderRadius: '50%',
  padding: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[500_32]}`
}));

const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '50%',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9
    }
  }
});

const PlaceholderStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.neutral,
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&:hover': { opacity: 0.72 }
}));

// ----------------------------------------------------------------------

interface CustomFile extends File {
  path?: string;
  preview?: string;
}

interface UploadAvatarProps extends DropzoneOptions {
  error?: boolean;
  file: CustomFile | string | null;
  value?: CustomFile | string | null;
  caption?: ReactNode;
  sx?: SxProps<Theme>;
  onChange?: Function;
}

export default function UploadAvatar({
  onChange: onFormChange,
  error,
  file,
  value,
  caption,
  sx,
  ...other
}: UploadAvatarProps) {
  const { enqueueSnackbar } = useSnackbar();

  const uploadToCloudinary = async (uploadFile: File) => {
    if (!cloudinaryConfig.cloudName || !cloudinaryConfig.uploadPreset) {
      throw new Error('Thiếu cấu hình Cloudinary. Vui lòng kiểm tra biến môi trường.');
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', cloudinaryConfig.folder);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error('Upload ảnh thất bại. Vui lòng thử lại.');
    }

    const responseData = await response.json();
    return responseData.secure_url as string;
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      try {
        const droppedFile = acceptedFiles[0];

        if (!droppedFile) {
          return;
        }

        const imageUrl = await uploadToCloudinary(droppedFile);
        if (onFormChange) {
          onFormChange(imageUrl);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message ?? 'Có lỗi', {
          variant: 'error'
        });
      }
    },
    [enqueueSnackbar, onFormChange]
  );

  // const onUploadFile = (e, onFormChange) => {
  //   setIsUploading(true);
  //   console.log('e', e);
  //   const file = e.target.files[0];
  //   const storageRef = ref(storage, `/files/${file.name}`);
  //   const uploadTask = uploadBytesResumable(storageRef, file);
  //   uploadTask.on(
  //     'state_changed',
  //     (snapshot) => {
  //       const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);

  //       // update progress
  //       // setPercent(percent);
  //     },
  //     (err) => {
  //       enqueueSnackbar(err.message ?? 'Có lỗi', {
  //         variant: 'error'
  //       });
  //       console.log(`err`, err);
  //     },
  //     () => {
  //       // download url
  //       getDownloadURL(uploadTask.snapshot.ref).then((url) => {
  //         console.log(url);
  //         onFormChange(url);
  //       });
  //     }
  //   );
  // };

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    onDrop,
    ...other
  });

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        my: 2,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
      }}
    >
      {fileRejections.map(({ file, errors }) => {
        const { path, size }: CustomFile = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return (
    <>
      <RootStyle sx={sx}>
        <DropZoneStyle
          {...getRootProps()}
          sx={{
            ...(isDragActive && { opacity: 0.72 }),
            ...((isDragReject || error) && {
              color: 'error.main',
              borderColor: 'error.light',
              bgcolor: 'error.lighter'
            })
          }}
        >
          <input {...getInputProps()} />

          {value && (
            <Box
              component="img"
              alt="avatar"
              src={isString(value) ? value : ''}
              sx={{ zIndex: 8, objectFit: 'cover' }}
            />
          )}

          <PlaceholderStyle
            className="placeholder"
            sx={{
              ...(value && {
                opacity: 0,
                color: 'common.white',
                bgcolor: 'grey.900',
                '&:hover': { opacity: 0.72 }
              })
            }}
          >
            <Box component={Icon} icon={roundAddAPhoto} sx={{ width: 24, height: 24, mb: 1 }} />
            <Typography variant="caption">{value ? 'Update photo' : 'Upload photo'}</Typography>
          </PlaceholderStyle>
        </DropZoneStyle>
      </RootStyle>

      {caption}

      {fileRejections.length > 0 && <ShowRejectionItems />}
    </>
  );
}
